import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Lecturas } from './screens/Lecturas';
import { CargaInicial } from './screens/CargaInicial';
import { api, type Group } from './api/client';
import { useSession } from './session/SessionContext';
import { Armazon } from './components/Armazon';
import { Portada } from './screens/Portada';
import { Panel } from './screens/Panel';
import { Usuarios } from './screens/Usuarios';
import { Grupos } from './screens/Grupos';
import { Perfil } from './screens/Perfil';
import { Correos } from './screens/Correos';
import { Catalogo } from './screens/Catalogo';
import { Documentos } from './screens/Documentos';
import { DocumentoDetalle } from './screens/DocumentoDetalle';
import { Consultas } from './screens/Consultas';
import { EmpleadoDetalle } from './screens/EmpleadoDetalle';
import { Bitacora } from './screens/Bitacora';
import { VisorRuta } from './screens/VisorRuta';
import { AvisoError } from './components/Aviso';
import { VerificacionPorCorreo } from './screens/VerificacionPorCorreo';
import { DispositivoPendiente } from './screens/DispositivoPendiente';
import { Dispositivos } from './screens/Dispositivos';
import { Equipo } from './screens/Equipo';

export function App() {
  const { t } = useTranslation();
  const { estado, puede } = useSession();
  const { pathname } = useLocation();
  const [grupos, setGrupos] = useState<Group[]>([]);
  const [error, setError] = useState<unknown>(null);

  const autenticado = estado.status === 'autenticado';

  // Se recarga a mano después de administrar grupos: la lista alimenta también
  // los selectores de documentos y usuarios, así que si no se refresca, un grupo
  // recién creado no aparece donde hace falta hasta recargar la página.
  const recargarGrupos = useCallback(() => {
    api.groups().then(setGrupos).catch(setError);
  }, []);

  useEffect(() => {
    if (!autenticado) return;
    recargarGrupos();
  }, [autenticado, recargarGrupos]);

  if (estado.status === 'cargando') {
    return (
      <div className="portada">
        <p className="tenue">{t('app.loading')}</p>
      </div>
    );
  }

  // La verificación por correo de quien administra y está terminando de entrar
  // sin su teléfono. Va antes que todo: puede llegar con una sesión anterior
  // todavía abierta en este navegador, y aun así lo que toca es terminar *este*
  // acceso —que, al completarse, cierra aquella—.
  if (pathname === '/verificacion') return <VerificacionPorCorreo />;
  // El dispositivo espera aprobación, o se le negó (ADR-0026). No hay sesión que
  // esperar: es todo lo que esa persona puede ver.
  if (pathname === '/dispositivo') return <DispositivoPendiente />;

  if (estado.status === 'anonimo') return <Portada />;

  return (
    <Routes>
      {/* El visor va fuera del armazón: en pantalla completa la barra lateral
          estorba, y en modo normal se pinta su propio marco. */}
      <Route path="/documentos/:documentId/leer" element={<VisorRuta />} />
      <Route path="/documentos/:documentId/leer/:versionId" element={<VisorRuta />} />

      <Route element={<Armazon />}>
        <Route index element={<Navigate to="/panel" replace />} />
        <Route
          path="/panel"
          element={
            <>
              <AvisoError error={error} />
              <Panel grupos={grupos} />
            </>
          }
        />
        <Route path="/documentos" element={<Documentos grupos={grupos} />} />
        <Route path="/documentos/:documentId" element={<DocumentoDetalle grupos={grupos} />} />
        <Route path="/catalogo" element={<Catalogo />} />
        {puede('audit.read') && (
          <Route path="/consultas" element={<Consultas grupos={grupos} />} />
        )}
        {puede('audit.read') && <Route path="/bitacora" element={<Bitacora />} />}
        {/* El detalle usa la misma pantalla: se abre desde el reporte y el
            único dato que cambia es de qué documento se habla. */}
        {puede('audit.read') && <Route path="/lecturas" element={<Lecturas />} />}
        {puede('audit.read') && <Route path="/lecturas/:documentId" element={<Lecturas />} />}
        {/* Gerencia: la lectura obligatoria de la gente de sus grupos. */}
        {puede('reading.team_report') && <Route path="/equipo" element={<Equipo />} />}
        <Route path="/grupos" element={<Grupos grupos={grupos} onCambio={recargarGrupos} />} />
        <Route path="/perfil" element={<Perfil />} />
        {puede('user.manage') && <Route path="/correos" element={<Correos />} />}
        {puede('system.initial_load') && <Route path="/carga-inicial" element={<CargaInicial />} />}
        {puede('user.invite') && <Route path="/usuarios" element={<Usuarios grupos={grupos} />} />}
        {puede('device.approve') && <Route path="/dispositivos" element={<Dispositivos />} />}
        {/* El comportamiento de lectura es dato de bitácora, no de administración
            de personas: lo abre quien puede auditar. */}
        {puede('audit.read') && <Route path="/usuarios/:userId" element={<EmpleadoDetalle />} />}
        {/* Una ruta que no existe no deja al usuario en blanco. */}
        <Route path="*" element={<Navigate to="/panel" replace />} />
      </Route>
    </Routes>
  );
}
